import { useEffect } from 'react';

interface PageMetaOptions {
  title: string;
  description: string;
  /**
   * Path starting with "/", e.g. "/", "/about"
   */
  path: string;
  imagePath?: string;
  imageAlt?: string;
  /** Sets <meta name="robots"> - e.g. "noindex, nofollow" for 404 pages */
  robots?: string;
}

export const SITE_ORIGIN = 'https://mortgagecomparecalculator.com';
const DEFAULT_SOCIAL_IMAGE = '/social-card.png';
const DEFAULT_SOCIAL_IMAGE_ALT = 'Mortgage Compare Calculator dashboard preview';

function upsertMeta(
  selector: string,
  attributes: Record<string, string>
) {
  if (typeof document === 'undefined') return;
  let el = document.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement('meta');
    document.head.appendChild(el);
  }
  Object.entries(attributes).forEach(([key, value]) => {
    el!.setAttribute(key, value);
  });
}

function upsertCanonical(url: string) {
  if (typeof document === 'undefined') return;
  let link = document.querySelector<HTMLLinkElement>("link[rel='canonical']");
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    document.head.appendChild(link);
  }
  link.setAttribute('href', url);
}

/**
 * Hook to keep document title, meta description, canonical and basic OG tags
 * in sync with the current React route.
 */
export function usePageMeta({
  title,
  description,
  path,
  imagePath = DEFAULT_SOCIAL_IMAGE,
  imageAlt = DEFAULT_SOCIAL_IMAGE_ALT,
  robots,
}: PageMetaOptions) {
  useEffect(() => {
    if (typeof document === 'undefined') return;

    const url = `${SITE_ORIGIN}${path}`;
    const imageUrl = imagePath.startsWith('http') ? imagePath : `${SITE_ORIGIN}${imagePath}`;

    document.title = title;

    upsertMeta("meta[name='description']", {
      name: 'description',
      content: description,
    });

    if (robots) {
      upsertMeta("meta[name='robots']", { name: 'robots', content: robots });
    } else {
      document.querySelector<HTMLMetaElement>("meta[name='robots']")?.remove();
    }

    upsertCanonical(url);

    // Basic Open Graph tags
    upsertMeta("meta[property='og:title']", {
      property: 'og:title',
      content: title,
    });

    upsertMeta("meta[property='og:description']", {
      property: 'og:description',
      content: description,
    });

    upsertMeta("meta[property='og:url']", {
      property: 'og:url',
      content: url,
    });

    upsertMeta("meta[property='og:type']", {
      property: 'og:type',
      content: 'website',
    });

    upsertMeta("meta[property='og:image']", {
      property: 'og:image',
      content: imageUrl,
    });

    upsertMeta("meta[property='og:image:alt']", {
      property: 'og:image:alt',
      content: imageAlt,
    });

    upsertMeta("meta[name='twitter:card']", {
      name: 'twitter:card',
      content: 'summary_large_image',
    });

    upsertMeta("meta[name='twitter:title']", {
      name: 'twitter:title',
      content: title,
    });

    upsertMeta("meta[name='twitter:description']", {
      name: 'twitter:description',
      content: description,
    });

    upsertMeta("meta[name='twitter:image']", {
      name: 'twitter:image',
      content: imageUrl,
    });

    upsertMeta("meta[name='twitter:image:alt']", {
      name: 'twitter:image:alt',
      content: imageAlt,
    });
  }, [title, description, path, imagePath, imageAlt, robots]);
}

